import React, { useState, useEffect } from "react"
import Popup from "../../component/popup/popup"
import Table, { TableColumn } from "../../component/table/table"
import { AgencyData, OfficeData } from "../../models"
import { useSnackbar } from "../../context/snackbarContext"
import { agent } from "../../apiClient/api"
import utilStyles from "../../asset/styles/util/util.module.scss"
import IconsColor from "../../util/iconsColor"
import CustomizedMenu from "component/muiMenu/muiMenu"
import OfficePopup from "./OfficePopup"
import styles from "../../asset/styles/agents/agents.module.scss"
import { toTitleCase } from "util/textcase"

type OfficesListPopupProps = {
    isOpen: boolean
    onClose: () => void
    agency: AgencyData | null
    onSuccess?: () => void
    initialOffices?: OfficeData[]
}

const OfficesListPopup: React.FC<OfficesListPopupProps> = ({ isOpen, onClose, agency, onSuccess, initialOffices }) => {
    const { showError, showSuccess } = useSnackbar()
    const [offices, setOffices] = useState<OfficeData[]>([])
    const [officePopupOpen, setOfficePopupOpen] = useState(false)
    const [officePopupMode, setOfficePopupMode] = useState<"add" | "edit">("add")
    const [selectedOffice, setSelectedOffice] = useState<OfficeData | null>(null)
    const [statusPopupOpen, setStatusPopupOpen] = useState(false)
    const [officeForStatus, setOfficeForStatus] = useState<OfficeData | null>(null)
    const [deletePopupOpen, setDeletePopupOpen] = useState(false)
    const [officeForDelete, setOfficeForDelete] = useState<OfficeData | null>(null)

    const GetOfficeList = async () => {
        if (!agency?.agency_id) return
        try {
            const response = await agent.OfficeListAPI.get(agency.agency_id, true)
            if (response?.status === true) {
                setOffices(response?.data ?? [])
            } else {
                showError(response?.error?.message || "Failed to load offices")
            }
        } catch (error) {
            console.log("GetOfficeList Error", error)
            showError("Failed to load offices")
        }
    }

    useEffect(() => {
        if (isOpen && agency) {
            if (initialOffices !== undefined) {
                setOffices(initialOffices)
            } else {
                GetOfficeList()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, agency, initialOffices])

    const columns: TableColumn<OfficeData>[] = [
        {
            key: "office_name",
            label: "Office Name",
            width: "20%",
            align: "left",
            render: (v) => toTitleCase(v)
        },
        {
            key: "address",
            label: "Address",
            width: "25%",
            align: "left",
            render: (value: string, row: OfficeData) => (
                <div>
                    <div>{toTitleCase(value)}</div>
                    <div style={{ fontSize: "12px", color: "#7a7a7a" }}>{toTitleCase(row.city_name)}</div>
                </div>
            ),
        },
        {
            key: "contact_number",
            label: "Contact Details",
            width: "20%",
            align: "left",
            render: (value: string, row: OfficeData) => (
                <div>
                    <div>{value}</div>
                    <div style={{ fontSize: "12px", color: "#7a7a7a" }}>{row.email}</div>
                </div>
            ),
        },
        {
            key: "flag_active",
            label: "Status",
            width: "10%",
            align: "left",
            render: (value: boolean) => (
                <span
                    className={
                        value === true
                            ? utilStyles.statusBadgeActive
                            : value === false
                              ? utilStyles.statusBadgeInactive
                              : utilStyles.statusBadgeInactive
                    }
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            key: "actions",
            label: "",
            width: "5%",
            align: "left",
            render: (_: unknown, row: OfficeData) => (
                <CustomizedMenu
                    buttonText=""
                    menuItems={[
                        {
                            label: "Edit",
                            onClick: () => {
                                setSelectedOffice(row)
                                setOfficePopupMode("edit")
                                setOfficePopupOpen(true)
                            },
                        },
                        {
                            label: `Set Status to ${row.flag_active ? "Inactive" : "Active"}`,
                            onClick: () => {
                                setOfficeForStatus(row)
                                setStatusPopupOpen(true)
                            },
                        },
                        {
                            label: "Delete",
                            onClick: () => {
                                if (offices.length <= 1) return
                                setOfficeForDelete(row)
                                setDeletePopupOpen(true)
                            },
                            style: offices.length <= 1
                                ? { color: "#bdbdbd", pointerEvents: "none" as const, cursor: "not-allowed" }
                                : { color: "#d32f2f" },
                        },
                    ]}
                    buttonVariant="text"
                    buttonStyle={{ padding: "0px", minWidth: "0px" }}
                    buttonEndIcon={
                        <IconsColor iconName="moreVert" width="20" height="20" fillColor="none" strokeColor="#7a7a7a" />
                    }
                />
            ),
        },
    ]

    const handleConfirmStatusChange = async () => {
        const officeToUpdate = officeForStatus
        setStatusPopupOpen(false)
        setOfficeForStatus(null)
        if (!officeToUpdate?.office_id) return
        try {
            const response = await agent.OfficeDeleteAPI.put({
                office_id: officeToUpdate.office_id,
                flag_active: !officeToUpdate.flag_active,
                office_flag_deleted: false,
            })
            if (response?.status === true) {
                showSuccess("Office status updated successfully")
                await GetOfficeList()
                onSuccess?.()
            } else {
                showError(response?.error?.message ?? "Failed to update office status")
            }
        } catch (err) {
            console.error("Update office status error", err)
            showError("Failed to update office status")
        }
    }

    const handleConfirmDelete = async () => {
        const officeToDelete = officeForDelete
        setDeletePopupOpen(false)
        setOfficeForDelete(null)
        if (!officeToDelete?.office_id) return
        if (offices.length <= 1) {
            showError("Cannot delete the last office of an agency")
            return
        }
        try {
            const response = await agent.OfficeDeleteAPI.put({
                office_id: officeToDelete.office_id,
                flag_active: false,
                office_flag_deleted: true,
            })
            if (response?.status === true) {
                showSuccess("Office deleted successfully")
                await GetOfficeList()
                onSuccess?.()
            } else {
                showError(response?.error?.message ?? "Failed to delete office")
            }
        } catch (err) {
            console.error("Delete office error", err)
            showError("Failed to delete office")
        }
    }

    const handleOfficeSuccess = () => {
        GetOfficeList()
        onSuccess?.()
    }

    return (
        <>
            <Popup
                isOpen={isOpen}
                onClose={onClose}
                title={`Offices - ${agency?.agency_name ?? ""}`}
                primaryButton={{
                    label: "Add Office",
                    onClick: () => {
                        setSelectedOffice(null)
                        setOfficePopupMode("add")
                        setOfficePopupOpen(true)
                    },
                }}
                size="large"
                heightFiexd={true}
            >
                <div style={{ paddingTop: 16 }}>
                    <Table
                        columns={columns}
                        data={offices}
                        keyExtractor={(row: OfficeData) => row.office_id ?? ""}
                        emptyMessage="No offices available"
                        minWidth={800}
                        showHeader={true}
                        maxHeight={500}
                    />
                </div>
            </Popup>

            <OfficePopup
                isOpen={officePopupOpen}
                onClose={() => {
                    setOfficePopupOpen(false)
                    setSelectedOffice(null)
                }}
                mode={officePopupMode}
                office={selectedOffice}
                agencyId={agency?.agency_id ?? ""}
                onSuccess={handleOfficeSuccess}
            />

            <Popup
                isOpen={statusPopupOpen}
                onClose={() => {
                    setStatusPopupOpen(false)
                    setOfficeForStatus(null)
                }}
                title="Change Office Status"
                primaryButton={{ label: "Confirm", onClick: handleConfirmStatusChange }}
                secondaryButton={{
                    label: "Cancel",
                    onClick: () => {
                        setStatusPopupOpen(false)
                        setOfficeForStatus(null)
                    },
                }}
                heightFiexd={true}
            >
                {officeForStatus && (
                    <div className={styles.confirmationContent}>
                        <IconsColor
                            iconName="warningSign"
                            width="80px"
                            height="80px"
                            fillColor="none"
                            strokeColor="#FF0000"
                        />
                        <p className={styles.confirmationText}>
                            Are you sure you want to change the status of <strong>{officeForStatus.office_name}</strong>{" "}
                            to <strong>{officeForStatus.flag_active ? "Inactive" : "Active"}</strong>?
                        </p>
                    </div>
                )}
            </Popup>

            <Popup
                isOpen={deletePopupOpen}
                onClose={() => {
                    setDeletePopupOpen(false)
                    setOfficeForDelete(null)
                }}
                title="Delete Office"
                primaryButton={{ label: "Delete", onClick: handleConfirmDelete }}
                secondaryButton={{
                    label: "Cancel",
                    onClick: () => {
                        setDeletePopupOpen(false)
                        setOfficeForDelete(null)
                    },
                }}
                heightFiexd={true}
            >
                {officeForDelete && (
                    <div className={styles.confirmationContent}>
                        <IconsColor
                            iconName="deleteIcon"
                            width="80px"
                            height="80px"
                            fillColor="none"
                            strokeColor="#d32f2f"
                        />
                        <p className={styles.confirmationText}>
                            Are you sure you want to permanently delete <strong>{officeForDelete.office_name}</strong>?
                            This action cannot be undone.
                        </p>
                    </div>
                )}
            </Popup>
        </>
    )
}

export default OfficesListPopup
